import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const KEY = 'jw_admin_view_as_customer';

const AdminViewContext = createContext(null);

export function AdminViewProvider({ children }) {
  const { user, loading } = useAuth();
  const [asCustomer, setAsCustomer] = useState(() => localStorage.getItem(KEY) === '1');

  // Auto-clear the "view as customer" mode whenever the logged-in user stops
  // being an admin (logout, or switching accounts). Do NOT clear while auth
  // is still resolving — otherwise a page reload wipes the flag before
  // AuthContext hydrates `user` and the pill / customer view flash out.
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'admin') {
      if (asCustomer) {
        localStorage.removeItem(KEY);
        setAsCustomer(false);
      }
    }
  }, [user, loading, asCustomer]);

  const setViewAsCustomer = useCallback((v) => {
    const val = !!v;
    setAsCustomer(val);
    if (val) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  }, []);

  const toggle = useCallback(() => setViewAsCustomer(!asCustomer), [asCustomer, setViewAsCustomer]);

  // A convenience flag: is the current user an admin AND choosing to browse
  // the site as a normal customer? Downstream components use this to render
  // the customer homepage instead of the marketing landing.
  const isAdminAsCustomer = !!(user && user.role === 'admin' && asCustomer);

  return (
    <AdminViewContext.Provider value={{ asCustomer, isAdminAsCustomer, setViewAsCustomer, toggle }}>
      {children}
    </AdminViewContext.Provider>
  );
}

export const useAdminView = () => useContext(AdminViewContext) || {
  asCustomer: false, isAdminAsCustomer: false, setViewAsCustomer: () => {}, toggle: () => {},
};
